import { useEffect, useRef, useState } from 'react';
import { NavIcon } from './NavIcons';

/**
 * "Report a problem" in the bar under a game. Sends a short note (no contact details; the server refuses
 * emails, links and phone numbers) to /api/suggest as a "Bug report", which shows on the owner's dashboard.
 */
export function ReportButton({ gameId }: { gameId: string }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const ref = useRef<HTMLSpanElement>(null);
  const max = 78 - gameId.length;

  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  const send = () => {
    const note = text.replace(/\s+/g, ' ').trim();
    if (note.length < 3) return;
    setState('sending');
    fetch('/api/suggest', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ kind: 'Bug report', idea: `${gameId}: ${note}`.slice(0, 80) }) })
      .then((r) => setState(r.ok ? 'sent' : 'error'))
      .catch(() => setState('error'));
  };

  return (
    <span className="controls-pop" ref={ref}>
      <button type="button" className="like-btn bar-icon" aria-expanded={open} aria-label="Report a problem" title="Report a problem" onClick={() => setOpen((o) => !o)}>
        <NavIcon name="flag" size={18} />
      </button>
      {open && (
        <div className="controls-pop__panel report" role="dialog" aria-label="Report a problem">
          {state === 'sent' ? (
            <p className="report__done">Thanks! We’ll take a look.</p>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <label htmlFor={`report-${gameId}`}>What went wrong?</label>
              <input
                id={`report-${gameId}`}
                value={text}
                maxLength={max}
                placeholder="e.g. the game froze after level 3"
                autoComplete="off"
                autoFocus
                onChange={(e) => {
                  setText(e.target.value);
                  if (state === 'error') setState('idle');
                }}
              />
              <p className="report__note">Please don’t type your name, email or any personal info.</p>
              {state === 'error' && <p className="report__err">Couldn’t send that. Please don’t include emails or links.</p>}
              <button type="submit" className="like-btn is-liked" disabled={state === 'sending' || text.trim().length < 3}>
                Send
              </button>
            </form>
          )}
        </div>
      )}
    </span>
  );
}
